import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '@/lib/api';
import { useSession } from '@/lib/session';
import { getTelegramUser, haptic } from '@/lib/telegram';
import Icon, { type IconName } from './icon';

const TABS: Array<{ to: string; key: string; icon: IconName; end?: boolean; badge?: boolean }> = [
  { to: '/', key: 'nav.home', icon: 'home', end: true },
  { to: '/categories', key: 'nav.catalog', icon: 'grid' },
  { to: '/cart', key: 'nav.cart', icon: 'cart', badge: true },
  { to: '/orders', key: 'nav.orders', icon: 'orders' },
  { to: '/profile', key: 'nav.profile', icon: 'user' },
];

/** Счётчики нужны и шапке, и вкладкам — держим один запрос на всё дерево. */
function useCounters() {
  const { authorized } = useSession();

  const cart = useQuery({
    queryKey: ['cart-count'],
    queryFn: async () => (await api.get<{ count: number }>('/cart/count')).data.count,
    enabled: authorized,
    refetchOnWindowFocus: true,
  });

  const favorites = useQuery({
    queryKey: ['favorites'],
    queryFn: async () => (await api.get<Array<{ id: string }>>('/favorites')).data,
    enabled: authorized,
  });

  return { cartCount: cart.data ?? 0, favoritesCount: favorites.data?.length ?? 0 };
}

function Counter({ value }: { value: number }) {
  return (
    <span className="absolute -right-1 -top-1 grid h-[18px] min-w-[18px] place-items-center rounded-pill bg-bordo px-1 text-[11px] font-semibold text-white">
      {value > 99 ? '99+' : value}
    </span>
  );
}

function Header() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { favoritesCount } = useCounters();
  const name = getTelegramUser()?.first_name;

  return (
    <header className="sticky top-0 z-30 bg-cream/95 backdrop-blur">
      <div className="px-4 pb-2 pt-3 sm:px-6">
        <div className="flex items-center justify-between gap-3">
          <p className="min-w-0 truncate text-[15px]">
            {name ? (
              <>
                {t('home.greeting')}, <span className="font-display font-semibold">{name}</span> 👋
              </>
            ) : (
              <span className="font-display text-lg font-semibold">{t('home.brand')}</span>
            )}
          </p>

          <button
            type="button"
            onClick={() => navigate('/favorites')}
            aria-label={t('nav.favorites')}
            className="relative grid size-11 shrink-0 place-items-center rounded-full bg-surface shadow-soft active:scale-95"
          >
            <Icon name="heart" size={20} className="text-bordo" filled={favoritesCount > 0} />
            {favoritesCount > 0 && <Counter value={favoritesCount} />}
          </button>
        </div>

        <button
          type="button"
          onClick={() => navigate('/search')}
          className="mt-3 flex w-full items-center gap-3 rounded-2xl bg-surface px-4 py-3 text-left shadow-soft active:scale-[0.99]"
        >
          <Icon name="search" size={20} className="text-muted" />
          <span className="text-[15px] text-muted">{t('home.searchPlaceholder')}</span>
        </button>
      </div>
    </header>
  );
}

function BottomNav() {
  const { t } = useTranslation();
  const { cartCount } = useCounters();

  return (
    <nav
      className="sticky bottom-0 z-30 border-t border-line bg-surface/95 backdrop-blur"
      style={{ paddingBottom: 'var(--tg-safe-area-inset-bottom, env(safe-area-inset-bottom))' }}
    >
      <div className="flex items-stretch justify-around px-2">
        {TABS.map((tab) => (
          <NavLink
            key={tab.to}
            to={tab.to}
            end={tab.end}
            onClick={() => haptic.tap()}
            className={({ isActive }) =>
              `flex flex-1 flex-col items-center gap-0.5 py-2 text-[11px] transition ${
                isActive ? 'text-bordo' : 'text-muted'
              }`
            }
          >
            <span className="relative">
              <Icon name={tab.icon} size={22} />
              {tab.badge && cartCount > 0 && <Counter value={cartCount} />}
            </span>
            {t(tab.key)}
          </NavLink>
        ))}
      </div>
    </nav>
  );
}

/** Вкладки нижнего меню: шапка с поиском сверху, навигация снизу. */
export function MainLayout() {
  return (
    <div className="mx-auto flex min-h-full max-w-app flex-col">
      <Header />
      <main className="flex-1">
        <Outlet />
      </main>
      <BottomNav />
    </div>
  );
}

/**
 * Вложенные экраны: карточка, чекаут, поиск. Нижнюю навигацию убираем —
 * она перекрывает основную кнопку Telegram и мешает вернуться назад.
 */
export function SubLayout() {
  return (
    <div className="mx-auto min-h-full max-w-app">
      <Outlet />
    </div>
  );
}
